import type { SeatsioConfig as GQLSeatsioConfig } from '../generated/checkout.graphql';
import { take } from '../util/takePayload';

export type SeatsioConfig = {
  workspaceKey: string;
  eventKey: string;
  region: 'na' | 'eu' | 'sa' | 'oc';
  objectWithoutPricingSelectable: boolean;
  publishedThumbnailUrl?: string;
  /**
   * Whether the backend judges this event able to offer best available seats at all. Plain
   * `boolean`, never optional: the wire value may be absent, and normalizing it here is what
   * lets every consumer read it as the decision it is.
   */
  bestAvailable: boolean;
};

/**
 * The generated type still predates `bestAvailable` — codegen reads the live dev backend, and
 * that one serves a schema older than the release candidate this rides. Declaring the field by
 * intersection keeps `src/generated` untouched (it is regenerated wholesale) while the mapper
 * still sees it; an optional property does not affect assignability, so callers passing a
 * generated config need no cast.
 */
type GQLSeatsioConfigPayload = GQLSeatsioConfig & { bestAvailable?: boolean | null };

export const fromGql = (config: GQLSeatsioConfigPayload): SeatsioConfig => ({
  ...(take(config, 'workspaceKey', 'eventKey', 'region', 'objectWithoutPricingSelectable', 'publishedThumbnailUrl') as SeatsioConfig),
  // Absent on a server that predates the field, `false` on a chart the backend judged unable to
  // offer best available — both have to read as "do not offer", so the coercion lives here
  // rather than at every consumer. Assigned outside `take`, which drops undefined keys and
  // would leave the declared boolean missing.
  bestAvailable: config.bestAvailable === true,
});
